import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import type { BaseLayerRecord } from '../lib/types';
import type { Calibration } from '../geo/types';
import {
  affineRotationDeg,
  cornerControlPoints,
  exactCalibration,
  fitAxisAligned,
  fitFullAffine,
  residuals,
} from '../geo/calibration';
import { num } from '../geo/format';
import { setState, toast, useCurrentMap, useStore } from '../store/store';
import { useCalibration } from './calibrationStore';
import { CALIBRATION_PRESETS } from './presets';
import { Icon } from '../panels/Icon';

async function imageSize(file: File): Promise<{ w: number; h: number }> {
  const bmp = await createImageBitmap(file);
  const out = { w: bmp.width, h: bmp.height };
  bmp.close();
  return out;
}

export function BaseLayersAdmin() {
  const { t } = useTranslation();
  const layers = useStore((s) => s.baseLayers);
  const calLayerId = useCalibration((s) => s.layerId);
  const calibrating = layers.find((l) => l.id === calLayerId);

  return (
    <div className="admin-layers">
      {calibrating ? (
        <CalibrationPanel layer={calibrating} />
      ) : (
        <>
          {layers.length === 0 && <p className="muted">{t('admin.layers.empty')}</p>}
          {layers.map((l, i) => (
            <LayerRow
              key={l.id}
              layer={l}
              first={i === 0}
              last={i === layers.length - 1}
              neighbours={layers}
            />
          ))}
          <UploadLayer />
        </>
      )}
    </div>
  );
}

function LayerRow({
  layer: l,
  first,
  last,
  neighbours,
}: {
  layer: BaseLayerRecord;
  first: boolean;
  last: boolean;
  neighbours: BaseLayerRecord[];
}) {
  const { t } = useTranslation();
  const save = async (patch: Partial<BaseLayerRecord>) => {
    try {
      await pb.collection('base_layers').update(l.id, patch);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const move = async (dir: -1 | 1) => {
    const idx = neighbours.findIndex((x) => x.id === l.id);
    const other = neighbours[idx + dir];
    if (!other) return;
    await save({ sortOrder: other.sortOrder });
    await pb
      .collection('base_layers')
      .update(other.id, { sortOrder: l.sortOrder === other.sortOrder ? l.sortOrder - dir : l.sortOrder });
  };
  const startCalibration = () => {
    const cal =
      l.calibration ??
      exactCalibration(
        useStore.getState().maps.find((m) => m.id === l.map)!.worldRect,
        l.widthPx,
        l.heightPx,
      );
    useCalibration.getState().set({ layerId: l.id, draft: cal, points: cal.controlPoints ?? [] });
    const view = useStore.getState().layerView;
    setState({ layerView: { ...view, [l.id]: { visible: true, opacity: 0.6 } } });
  };
  const cal = l.calibration;
  return (
    <div className="card">
      <div className="row">
        <strong className="ellipsis">{l.name}</strong>
        <span className="muted small">
          {t(`admin.layers.sources.${l.source}`)} · {l.widthPx}×{l.heightPx}
        </span>
      </div>
      <div className="muted small">
        {cal
          ? cal.mode === 'exact'
            ? t('admin.layers.exact')
            : t('admin.layers.fitted', {
                n: cal.controlPoints?.length ?? 0,
                rms: cal.rms != null ? num(cal.rms, 1) : '—',
              })
          : t('admin.layers.uncalibrated')}
      </div>
      <div className="row wrap">
        <label className="check-row small">
          <input
            type="checkbox"
            checked={l.visibleByDefault}
            onChange={() => void save({ visibleByDefault: !l.visibleByDefault })}
          />
          {t('admin.layers.visibleByDefault')}
        </label>
        <div className="spacer" />
        <button
          className="small"
          disabled={first}
          onClick={() => void move(-1)}
          title={t('admin.layers.down')}
        >
          ↓
        </button>
        <button className="small" disabled={last} onClick={() => void move(1)} title={t('admin.layers.up')}>
          ↑
        </button>
        {l.source !== 'unity_render' && (
          <button className="small" onClick={startCalibration}>
            <Icon id="tool-calibrate" /> {t('admin.layers.calibrate')}
          </button>
        )}
        <button
          className="small danger"
          onClick={() => {
            if (confirm(t('admin.layers.confirmDelete', { name: l.name })))
              void pb.collection('base_layers').delete(l.id);
          }}
        >
          <Icon id="trash" />
        </button>
      </div>
    </div>
  );
}

function UploadLayer() {
  const { t } = useTranslation();
  const map = useCurrentMap();
  const layers = useStore((s) => s.baseLayers);
  const user = useStore((s) => s.user);
  const [file, setFile] = useState<File | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [name, setName] = useState('');
  const [source, setSource] = useState<BaseLayerRecord['source']>('painted');
  const [busy, setBusy] = useState(false);

  const pickFile = async (picked: File | null) => {
    setFile(picked);
    setSize(null);
    if (!picked) return;
    setName((n) => n || picked.name.replace(/\.[^.]+$/, ''));
    try {
      setSize(await imageSize(picked));
    } catch {
      toast(t('admin.layers.notAnImage'), 'error');
    }
  };

  const preset = useMemo(
    () =>
      CALIBRATION_PRESETS.find(
        (p) => map && p.mapSlug === map.slug && size && p.widthPx === size.w && p.heightPx === size.h,
      ),
    [map, size],
  );

  const upload = async () => {
    if (!file || !size || !map) return;
    setBusy(true);
    try {
      let calibration: Calibration;
      if (preset) {
        calibration = {
          mode: 'fitted',
          affine: preset.affine,
          controlPoints: cornerControlPoints(
            preset.affine,
            size.w,
            size.h,
            'measured silhouette fit 2026-09-25',
          ),
          rms: 0,
          note: preset.id,
        };
      } else {
        calibration = exactCalibration(map.worldRect, size.w, size.h);
        calibration.mode = 'fitted';
      }
      const fd = new FormData();
      fd.append('map', map.id);
      fd.append('name', name || file.name);
      fd.append('kind', 'image');
      fd.append('image', file);
      fd.append('widthPx', String(size.w));
      fd.append('heightPx', String(size.h));
      fd.append('source', source);
      fd.append('opacity', '1');
      fd.append('visibleByDefault', 'true');
      fd.append('sortOrder', String(layers.reduce((m, l) => Math.max(m, l.sortOrder), 0) + 1));
      fd.append('calibration', JSON.stringify(calibration));
      if (user) fd.append('createdBy', user.id);
      await pb.collection('base_layers').create(fd);
      setFile(null);
      setName('');
      toast(t('admin.layers.uploaded'));
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="group-label">{t('admin.layers.upload')}</div>
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => void pickFile(e.target.files?.[0] ?? null)}
      />
      {size && (
        <div className="muted small">
          {size.w}×{size.h} px
        </div>
      )}
      <label>
        {t('common.name')}
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        {t('admin.layers.source')}
        <select value={source} onChange={(e) => setSource(e.target.value as BaseLayerRecord['source'])}>
          {(['painted', 'sketch', 'unity_render', 'other'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`admin.layers.sources.${s}`)}
            </option>
          ))}
        </select>
      </label>
      <p className="muted small">{preset ? t('admin.layers.presetFound') : t('admin.layers.stretchNote')}</p>
      <button className="primary" disabled={!file || !size || busy} onClick={() => void upload()}>
        <Icon id="tool-upload" /> {t('admin.layers.uploadButton')}
      </button>
    </div>
  );
}

function CalibrationPanel({ layer }: { layer: BaseLayerRecord }) {
  const { t } = useTranslation();
  const map = useCurrentMap();
  const { draft, points, set } = useCalibration();
  const tool = useStore((s) => s.tool);
  const picking = tool.kind === 'calibrate';

  // Refit whenever control points change.
  useEffect(() => {
    if (points.length >= 2) {
      const fit = fitAxisAligned(points);
      if (fit) set({ draft: fit });
    }
  }, [points, set]);

  if (!draft || !map) return null;
  const full = points.length >= 3 ? fitFullAffine(points) : null;
  const rotation = full ? affineRotationDeg(full) : null;
  const res = residuals({ ...draft, controlPoints: points });

  const setAffine = (i: number, v: number) => {
    if (!isFinite(v)) return;
    const a = [...draft.affine] as Calibration['affine'];
    a[i] = v;
    set({ draft: { ...draft, affine: a } });
  };
  const stop = () => {
    set({ layerId: null, draft: null, points: [] });
    setState({ tool: { kind: 'none' } });
  };
  const save = async () => {
    try {
      await pb.collection('base_layers').update(layer.id, {
        calibration: {
          ...draft,
          mode: 'fitted',
          controlPoints: points,
          rms: res.length ? Math.sqrt(res.reduce((s, r) => s + r * r, 0) / res.length) : null,
        },
      });
      toast(t('calibration.saved'));
      stop();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const preset = CALIBRATION_PRESETS.find(
    (p) => p.mapSlug === map.slug && p.widthPx === layer.widthPx && p.heightPx === layer.heightPx,
  );

  return (
    <div className="calibration">
      <h3>
        {t('calibration.title')}: {layer.name}
      </h3>
      <p className="muted small">{t('calibration.help')}</p>
      <div className="row wrap">
        <button
          className={picking ? 'active' : 'primary'}
          onClick={() =>
            setState({
              tool: picking ? { kind: 'none' } : { kind: 'calibrate', layerId: layer.id, step: 'image' },
            })
          }
        >
          {picking ? t('calibration.stopPicking') : t('calibration.addPoint')}
        </button>
        <button
          onClick={() =>
            set({ draft: exactCalibration(map.worldRect, layer.widthPx, layer.heightPx), points: [] })
          }
        >
          {t('calibration.stretch')}
        </button>
        {preset && (
          <button
            onClick={() =>
              set({
                draft: { mode: 'fitted', affine: preset.affine, controlPoints: [] },
                points: cornerControlPoints(
                  preset.affine,
                  layer.widthPx,
                  layer.heightPx,
                  'measured silhouette fit 2026-09-25',
                ),
              })
            }
          >
            {t('calibration.preset')}
          </button>
        )}
      </div>

      <div className="numeric">
        {(
          [
            [0, 'calibration.scaleX'],
            [2, 'calibration.offsetX'],
            [4, 'calibration.scaleZ'],
            [5, 'calibration.offsetZ'],
          ] as const
        ).map(([i, label]) => (
          <label key={i}>
            {t(label)}
            <input
              type="number"
              step={i % 2 === 0 && i !== 2 ? 0.0005 : 1}
              value={Number(draft.affine[i].toFixed(5))}
              onChange={(e) => setAffine(i, Number(e.target.value))}
            />
          </label>
        ))}
      </div>

      <table className="vertices small">
        <thead>
          <tr>
            <th>#</th>
            <th>px, py</th>
            <th>X, Z</th>
            <th>{t('calibration.error')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {points.map((p, i) => (
            <tr key={i} className={res[i] > 25 ? 'changed' : ''}>
              <td>{i + 1}</td>
              <td className="mono">
                {num(p.px, 0)}, {num(p.py, 0)}
              </td>
              <td className="mono">
                {num(p.x, 1)}, {num(p.z, 1)}
              </td>
              <td className="mono">{num(res[i] ?? 0, 1)} m</td>
              <td>
                <button
                  className="ghost icon-only"
                  onClick={() => set({ points: points.filter((_, j) => j !== i) })}
                >
                  <Icon id="close" size={12} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {points.length > 0 && (
        <p className="small">
          {t('calibration.rms', {
            rms: num(Math.sqrt(res.reduce((s, r) => s + r * r, 0) / Math.max(1, res.length)), 1),
          })}
          {rotation !== null && Math.abs(rotation) > 1 && (
            <span className="warn-text"> {t('calibration.rotationWarning', { deg: num(rotation, 1) })}</span>
          )}
        </p>
      )}
      <div className="save-row">
        <button onClick={stop}>{t('common.cancel')}</button>
        <button className="primary" onClick={() => void save()}>
          {t('calibration.save')}
        </button>
      </div>
    </div>
  );
}
